import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { TitleService } from '../../shared/services/title.service';
import { AuthService, ILoginDetails } from '../../shared/services/auth/auth.service';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import { GaValidateService } from '../../shared/formly/ga-validate.service';
import { Subscription } from 'rxjs';
import { TranslationService } from '../../shared/translation/translation.service';

const VALIDATION_KEY_LOGGED_ON:string = 'logged-on';

@Component({
  selector: 'login',
  template: `
    <div class="alert alert-danger login-failure" *ngIf="loginFailure">
      <i class="fa fa-exclamation-circle"></i>
      {{ loginFailure }}
    </div>

    <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true">
      <button class="btn" (click)="login()" [disabled]="invalidForm || loginDisabled" [busyButton]="signingIn">
        <span translate>APP.ACCESS.LOGIN.BUTTON</span>
      </button>
      <span class="checking-login" *ngIf="checkingLogon"><activity-indicator></activity-indicator>Checking login state</span>

      <ul class="other-options">
        <li><a href="" uiSref="access.forgotpassword" translate>APP.ACCESS.FORGOT_PASSWORD</a></li>
        <li class="create-account-option"><a href="" uiSref="access.signup" translate>APP.ACCESS.LOGIN.CREATE_ACCOUNT</a></li>
      </ul>
    </formly>
  `
})
export class LoginComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public loginAs:string;

  public loginFailure:string;
  public signingIn:boolean;
  public fields:IExtraFieldConfigurationObject[];

  public model:{
    username?:string;
    password?:string;
    remember?:boolean;
    as?:string;
  } = {};

  public checkingLogon:boolean;
  public loginDisabled:boolean;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public invalidForm:boolean = true;

  private passwordFocus:boolean;
  private logonAsField:IExtraFieldConfigurationObject;
  private validationLoggedOnMessage:string;
  private onAuthenticatedSubscription:Subscription;
  private onAuthFailureSubscription:Subscription;
  private onFormStatusChange:Subscription;

  constructor(
    private authService:AuthService,
    private validateService:GaValidateService,
    private formlyService:GaFormlyService,
    private translationService:TranslationService,
    titleService:TitleService
  ) {
    titleService.setTitle('Login');
  }

  public ngOnInit():void {
    const loginDetails:ILoginDetails = this.authService.getLoginDetails();
    this.validationLoggedOnMessage = this.translationService.instant('APP.ACCESS.LOGIN.VALIDATION_LOGGED_IN');

    this.model = {
      username: loginDetails.username,
      password: null,
      remember: loginDetails.remember
    };

    if (this.loginAs !== 'true') {
      this.model.as = this.loginAs;
    }

    this.loginFailure = undefined;
    this.signingIn = false;

    // create formly fields
    this.createFields();

    // register listener
    this.listen();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.loginAs) {
      this.loginAs = changes.loginAs.currentValue;
    }
  }

  private listen():void {
    this.onAuthenticatedSubscription = this.authService.onAuthenticated.subscribe((isAuthenticated:boolean) => {
        this.signingIn = false;
        if (isAuthenticated) {
          this.loginFailure = undefined;
        }
      });

    this.onAuthFailureSubscription = this.authService.onAuthFailure.subscribe((message:string) => {
        this.signingIn = false;
        this.loginFailure = message || 'Login failed';
        this.passwordFocus = true;
      });

    this.onFormStatusChange = this.form.statusChanges.subscribe(() => {
      // doing here instead directly from template prevent lifecycle error
      // https://github.com/angular/angular/issues/23657
      this.invalidForm = this.form.invalid;
    });
  }

  private createFields():void {
    this.passwordFocus = false;
    let usernameFocus:boolean = false;

    if (this.model.username) {
      this.passwordFocus = true;
    } else {
      usernameFocus = true;
    }

    const labelClass:string = 'col-sm-4';
    const valueClass:string = 'col-sm-8';

    this.fields = [
      this.formlyService.anInput('username', 'GA.GENERAL.USERNAME')
        .withId('username')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            inputFocus: usernameFocus
          },
          validation: {
            show: false
          }
        })
        .build(),
      this.formlyService.anInput('password', 'APP.ACCESS.PASSWORD_TEXT')
        .withId('password')
        .withInputType('password')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          templateOptions: {
            inputFocus: this.passwordFocus
          },
          validation: {
            show: false
          }
        })
        .build(),
      this.formlyService.aCheckbox('remember', 'APP.ACCESS.LOGIN.REMEMBER')
        .withoutWrapper()
        .withWrapperClass('group')
        .build()
    ];

    if (this.loginAs) {
      this.logonAsField = this.formlyService.anInput('as', 'APP.ACCESS.LOGIN.SIGN_IN_AS')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withValidationMessage(VALIDATION_KEY_LOGGED_ON, this.validationLoggedOnMessage)
        .build();
      this.fields[ 2 ] = this.logonAsField;
      this.checkLogon();
    }
  }

  private checkLogon():void {
    this.checkingLogon = true;
    this.loginDisabled = true;

    this.authService.isLoggedOnInOtherTab()
      .then((loggedOn:boolean) => {
        if (loggedOn) {
          // because in ngx form control disabled input won't display validation message, so we show custom validation message instead from to.validationMessage
          this.logonAsField.formControl.setErrors({ [ VALIDATION_KEY_LOGGED_ON ]: this.validationLoggedOnMessage });
          this.logonAsField.templateOptions.validationErrorMessage = this.validationLoggedOnMessage;
          this.logonAsField.validation.show = true;

          // make the field unusable
          this.logonAsField.templateOptions.disabled = true;
          this.logonAsField.formControl.patchValue(undefined);
        }
      })
      .finally(() => {
        this.checkingLogon = false;
        this.loginDisabled = false;
      });
  }

  public login():void {
    const performLogin:Function = () => {
      this.signingIn = true;
      this.loginFailure = undefined;
      this.authService.performLogin(this.model.username, this.model.password, this.model.remember, this.model.as);
    };

    // show form errors when the login form is invalid,
    // but ignore LOGON_AS_NOT_ALLOWED, because model value will be undefined => only normal login is possible
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => performLogin())
      .catch((errors:string[]) => {
        if (errors.length !== 1 || errors[ 0 ] !== this.validationLoggedOnMessage) {
          return;
        }

        performLogin();
      });
  }

  public ngOnDestroy():void {
    this.onAuthenticatedSubscription?.unsubscribe();
    this.onAuthFailureSubscription?.unsubscribe();
    this.onFormStatusChange?.unsubscribe();
  }
}